import {
	PHASE,
	NO_LOTTERY_DRAFT_TYPES,
	ALL_STAR_GAME_ONLY,
	TIME_BETWEEN_GAMES,
} from "../../../common/constants.ts";
import { draft, season } from "../core/index.ts";
import g from "./g.ts";
import getMobileTabAlerts from "./getMobileTabAlerts.ts";
import helpers from "./helpers.ts";
import local from "./local.ts";
import lock from "./lock.ts";
import toUI from "./toUI.ts";
import type { Option } from "../../../common/types.ts";
import { idb } from "../db/index.ts";

const updatePlayMenu = async () => {
	if (process.env.NODE_ENV === "test") {
		return;
	}

	const allOptions = {
		stop: {
			label: "Stop",
			labelKey: "playMenu.stop",
		},
		day: {
			label: "One day",
			labelKey: "playMenu.day",
		},
		week: {
			label: "One week",
			labelKey: "playMenu.week",
		},
		month: {
			label: "One month",
			labelKey: "playMenu.month",
		},
		untilAllStarGame: {
			label: `Until All-Star ${ALL_STAR_GAME_ONLY ? "game" : "events"}`,
			labelKey: ALL_STAR_GAME_ONLY
				? "playMenu.untilAllStarGameOnly"
				: "playMenu.untilAllStarEvents",
		},
		untilTradeDeadline: {
			label: "Until trade deadline",
			labelKey: "playMenu.untilTradeDeadline",
		},
		viewAllStar: {
			url: helpers.leagueUrl(["daily_schedule"]),
			label: `All-Star ${ALL_STAR_GAME_ONLY ? "teams" : "events"}`,
			labelKey: ALL_STAR_GAME_ONLY
				? "playMenu.viewAllStarTeams"
				: "playMenu.viewAllStarEvents",
		},
		untilPlayoffs: {
			label: "Until playoffs",
			labelKey: "playMenu.untilPlayoffs",
		},
		untilEndOfRound: {
			label: "Until end of round",
			labelKey: "playMenu.untilEndOfRound",
		},
		untilEndOfPlayIn: {
			label: "Until end of play-in tournament",
			labelKey: "playMenu.untilEndOfPlayIn",
		},
		throughPlayoffs: {
			label: "Through playoffs",
			labelKey: "playMenu.throughPlayoffs",
		},
		dayLive: {
			url: helpers.leagueUrl(["daily_schedule", "today"]),
			label: `One ${TIME_BETWEEN_GAMES} (live)`,
			labelKey:
				TIME_BETWEEN_GAMES === "week" ? "playMenu.weekLive" : "playMenu.dayLive",
		},
		viewDraftLottery: {
			url: helpers.leagueUrl(["draft_lottery"]),
			label: "View draft lottery",
			labelKey: "playMenu.viewDraftLottery",
		},
		untilDraft: {
			label: "Until draft",
			labelKey: "playMenu.untilDraft",
		},
		onePick: {
			label: "One pick",
			labelKey: "playMenu.onePick",
		},
		untilYourNextPick: {
			label: "Until your next pick",
			labelKey: "playMenu.untilYourNextPick",
		},
		untilEnd: {
			label: "Until end of draft",
			labelKey: "playMenu.untilEnd",
		},
		viewDraft: {
			url: helpers.leagueUrl(["draft"]),
			label: "View draft",
			labelKey: "playMenu.viewDraft",
		},
		untilResignPlayers: {
			label:
				g.get("salaryCapType") === "hard" || !g.get("draftPickAutoContract")
					? "Re-sign players and sign rookies"
					: "Re-sign players with expiring contracts",
			labelKey:
				g.get("salaryCapType") === "hard" || !g.get("draftPickAutoContract")
					? "playMenu.resignPlayersHard"
					: "playMenu.resignPlayersSoft",
		},
		untilFreeAgency: {
			label: "Until free agency",
			labelKey: "playMenu.untilFreeAgency",
		},
		untilPreseason: {
			label: "Until preseason",
			labelKey: "playMenu.untilPreseason",
		},
		untilRegularSeason: {
			label: "Until regular season",
			labelKey: "playMenu.untilRegularSeason",
		},
		contractNegotiationList: {
			url: helpers.leagueUrl(["negotiation"]),
			label: "Continue re-signing players",
			labelKey: "playMenu.contractNegotiationList",
		},
		message: {
			// M3-5: the standalone /message route is retired into the home InboxSheet
			// overlay. Land on the league home surface with the one-shot
			// ?m_open_latest=1 param so the dashboard opens the sheet straight on the
			// latest pending letter (reproducing the old no-mid auto-redirect).
			url: `${helpers.leagueUrl([])}?m_open_latest=1`,
			label: "Read new message",
			labelKey: "playMenu.message",
		},
		newLeague: {
			url: "/new_league",
			label: "Try again in a new league",
			labelKey: "playMenu.newLeague",
		},
		seasonSummary: {
			url: helpers.leagueUrl(["history"]),
			label: "View season summary",
			labelKey: "playMenu.seasonSummary",
		},
		expansionDraft: {
			url: helpers.leagueUrl(["protect_players"]),
			label: "Continue expansion draft setup",
			labelKey: "playMenu.expansionDraft",
		},
	} satisfies Record<
		string,
		{
			label: string;
			labelKey: string;
			labelKeyParams?: Record<string, string | number>;
			url?: string;
		}
	>;

	let keys: (keyof typeof allOptions)[] = [];

	if (
		g.get("phase") === PHASE.DRAFT ||
		g.get("phase") === PHASE.FANTASY_DRAFT ||
		g.get("phase") === PHASE.EXPANSION_DRAFT
	) {
		const draftPicks = await draft.getOrder();
		const nextPick = draftPicks[0];

		if (
			nextPick &&
			g.get("userTids").includes(nextPick.tid) &&
			!g.get("spectator")
		) {
			keys = ["viewDraft"];
		} else if (draftPicks.some((dp) => g.get("userTids").includes(dp.tid))) {
			keys = ["onePick", "untilYourNextPick", "viewDraft"];
		} else {
			keys = ["onePick", "untilEnd", "viewDraft"];
		}
	} else if (g.get("phase") === PHASE.PRESEASON) {
		// Preseason
		keys = ["untilRegularSeason"];
	} else if (
		g.get("phase") === PHASE.REGULAR_SEASON ||
		g.get("phase") === PHASE.AFTER_TRADE_DEADLINE
	) {
		const untilMore: typeof keys = [];

		const schedule = await season.getSchedule();
		const tradeDeadlineIndex = schedule.findIndex(
			(game) => game.awayTid === -3 && game.homeTid === -3,
		);
		const allStarIndex = schedule.findIndex(
			(game) => game.awayTid === -2 && game.homeTid === -1,
		);

		// > rather than >= because if it's the next game already, no need to "play until"
		if (tradeDeadlineIndex > 0 && allStarIndex > 0) {
			if (tradeDeadlineIndex < allStarIndex) {
				untilMore.push("untilTradeDeadline", "untilAllStarGame");
			} else {
				untilMore.push("untilAllStarGame", "untilTradeDeadline");
			}
		} else if (tradeDeadlineIndex > 0) {
			untilMore.push("untilTradeDeadline");
		} else if (allStarIndex > 0) {
			untilMore.push("untilAllStarGame");
		}

		// Regular season - pre trading deadline
		keys = [
			"day",
			"dayLive",
			"week",
			"month",
			...untilMore,
			"untilPlayoffs",
		];

		if (allStarIndex === 0) {
			keys.unshift("viewAllStar");
		}
	} else if (g.get("phase") === PHASE.PLAYOFFS) {
		// Playoffs
		keys = [
			"day",
			"dayLive",
			"untilEndOfRound",
			"untilEndOfPlayIn",
			"throughPlayoffs",
		];

		// If playoff contains no rounds with more than one game, then untilEndOfRound is not needed
		const maxGames = Math.max(...g.get("numGamesPlayoffSeries", "current"));
		if (maxGames <= 1) {
			keys = keys.filter((key) => key !== "untilEndOfRound");
		}

		if (g.get("playIn")) {
			const playoffSeries = await idb.cache.playoffSeries.get(g.get("season"));
			if (!playoffSeries || playoffSeries.currentRound > -1) {
				keys = keys.filter((key) => key !== "untilEndOfPlayIn");
			}
		} else {
			keys = keys.filter((key) => key !== "untilEndOfPlayIn");
		}

		const schedule = await season.getSchedule();
		const allStarIndex = schedule.findIndex(
			(game) => game.awayTid === -2 && game.homeTid === -1,
		);
		if (allStarIndex === 0) {
			keys.unshift("viewAllStar");
		}
	} else if (g.get("phase") === PHASE.DRAFT_LOTTERY) {
		const repeatSeasonType = g.get("repeatSeason")?.type;
		if (
			repeatSeasonType === "playersAndRosters" ||
			g.get("forceHistoricalRosters")
		) {
			keys = ["untilPreseason"];
		} else {
			if (
				g.get("draftType") === "freeAgents" ||
				repeatSeasonType === "players"
			) {
				// For draftType freeAgents - special case in actions.ts will call the draft phases before this automatically
				// For repeatSeasonType players - there is no draft
				keys = ["untilResignPlayers"];
			} else {
				// Offseason - pre draft
				keys = !NO_LOTTERY_DRAFT_TYPES.has(g.get("draftType"))
					? ["viewDraftLottery", "untilDraft"]
					: ["untilDraft"];
			}
		}
	} else if (g.get("phase") === PHASE.AFTER_DRAFT) {
		// Offseason - post draft
		keys = ["untilResignPlayers"];
	} else if (g.get("phase") === PHASE.RESIGN_PLAYERS) {
		// Offseason - re-sign players
		keys = ["contractNegotiationList", "untilFreeAgency"];
	} else if (g.get("phase") === PHASE.FREE_AGENCY) {
		// Offseason - free agency
		keys = ["day", "week", "untilPreseason"];
	}

	const unreadMessage = await lock.unreadMessage();

	if (g.get("expansionDraft").phase === "protection") {
		keys = ["expansionDraft"];
	}

	if (unreadMessage) {
		keys = ["message"];
	}

	if (local.unviewedSeasonSummary) {
		keys = ["seasonSummary"];
	}

	if (lock.get("gameSim")) {
		keys = ["stop"];
	}

	if (lock.get("newPhase")) {
		keys = [];
	}

	// If there is an unread message, it's from the owner saying the player is fired, so let the user see that first.
	if (g.get("gameOver") && !unreadMessage) {
		keys = ["newLeague"];
	}

	const someOptions: Option[] = keys.map((id) => {
		return {
			...allOptions[id],
			id,
		};
	});

	const mobileTabAlerts = await getMobileTabAlerts();

	toUI("updateLocal", [
		{
			mobileTabAlerts,
			playMenuOptions: someOptions,
		},
	]);
};

export default updatePlayMenu;
